import { memo } from "react";
import { IDropdownOption } from "@fluentui/react";
import { ToolBarProductSettingOptions } from "../../../components/ToolBarProductSettingOptions";
import {
    Product as FilterProduct,
    ProductVersion as FilterProductVersion
} from "../../../components/ToolBarProductSettingOptions/ToolBarProductSettingOptions";
import { AnalysisPanelTabs } from "../../../components/AnalysisPanel";
import { ChatModel, ReasoningEffort } from "../../../api/models";
import { Product, SubProduct, ProductVersion } from "../utils/productFilterHelpers";
import styles from "../Chat.module.css";

export interface ChatToolbarProps {
    products: Product[];
    productText: string;
    subProductText: string;
    productVersionText: string;
    productIndexes: { manual_key: string; faq_key: string };
    subProductIndexes: { manual_sub_key: string; faq_sub_key: string };
    productVersionIndexes: { manual_version_key: string; faq_version_key: string };
    windowSize: { width: number; height: number };
    shouldDisableInputBox: () => boolean;
    onProductClick: (product: Product) => void;
    onSubProductClick: (
        subProduct: SubProduct,
        isSubOnly: boolean,
        lastHoveredRef: Product | undefined,
        lastHoveredVersionRef: ProductVersion | undefined
    ) => void;
    onVersionClick: (version: ProductVersion, lastHoveredRef: Product | undefined) => void;
    handleTopPosition: (refs: unknown, index: number, versionsLength?: number) => string;
    isWapUser: boolean;
    isAdmin: boolean;
    enableAllProductForAllUsers?: boolean;
    enableAllProductForUser?: boolean;
    docTypeFilter: string;
    categories: { key: string; text: string }[];
    chatModel: ChatModel;
    reasoningEffort?: ReasoningEffort;
    aiModel_admin: IDropdownOption[];
    templateAppliedFlagValue: boolean;
    activeAnalysisPanelTab: AnalysisPanelTabs | undefined;
    applyRestriction: boolean;
    isStreaming: boolean;
    isLoading: boolean;
    isAnswerLoading: boolean;
    activateTemplateTour: boolean;
    setActivateTemplateTour: (value: boolean) => void;
    styles?: Record<string, string>;
    onDocTypeFilterChange: (_event: unknown, item: { key?: string | number } | undefined) => void;
    onChatModelChange: (_event: React.FormEvent<HTMLDivElement> | undefined, option: IDropdownOption<ChatModel> | undefined) => void;
    onReasoningEffortChange?: (_event: React.FormEvent<HTMLDivElement> | undefined, item: IDropdownOption<ReasoningEffort> | undefined) => void;
    onConfigPanelToggle: () => void;
    onTemplateIconClick: () => void;
    onTemplateClear: () => void;
    saveUserSettings: (settings: object) => Promise<void>;
}

const ChatToolbarComponent: React.FC<ChatToolbarProps> = ({
    products,
    productText,
    subProductText,
    productVersionText,
    productIndexes,
    subProductIndexes,
    productVersionIndexes,
    windowSize,
    shouldDisableInputBox,
    onProductClick,
    onSubProductClick,
    onVersionClick,
    handleTopPosition,
    isWapUser,
    isAdmin,
    enableAllProductForAllUsers,
    enableAllProductForUser,
    docTypeFilter,
    categories,
    chatModel,
    reasoningEffort,
    aiModel_admin,
    templateAppliedFlagValue,
    activeAnalysisPanelTab,
    applyRestriction,
    isStreaming,
    isLoading,
    isAnswerLoading,
    activateTemplateTour,
    setActivateTemplateTour,
    styles: toolbarStyles,
    onDocTypeFilterChange,
    onChatModelChange,
    onReasoningEffortChange,
    onConfigPanelToggle,
    onTemplateIconClick,
    onTemplateClear,
    saveUserSettings
}) => {
    const toChatVersion = (version: FilterProductVersion): ProductVersion => ({
        manual_version_key: version.manual_version_key,
        faq_version_key: version.faq_version_key,
        text: version.text,
        sub_versions: version.sub_versions || [],
        faq_sub_key: undefined
    });

    const toChatProduct = (product: FilterProduct): Product => ({
        manual_key: product.manual_key,
        faq_key: product.faq_key,
        text: product.text,
        versions: (product.versions || []).map((version: FilterProductVersion) => toChatVersion(version)),
        is_sub_only: product.is_sub_only,
        faq_groupids: (product as Partial<Product>).faq_groupids || [],
        manual_groupids: (product as Partial<Product>).manual_groupids || []
    });

    return (
        <div className={styles.toolbarContainer}>
            <ToolBarProductSettingOptions
                products={products}
                productText={productText}
                subProductText={subProductText}
                productVersionText={productVersionText}
                productIndexes={productIndexes}
                subProductIndexes={subProductIndexes}
                productVersionIndexes={productVersionIndexes}
                windowSize={windowSize}
                shouldDisableInputBox={shouldDisableInputBox}
                onProductClick={(product: FilterProduct) => {
                    onProductClick(toChatProduct(product));
                }}
                onSubProductClick={(
                    subProduct: SubProduct,
                    isSubOnly: boolean,
                    lastHoveredRef?: FilterProduct,
                    lastHoveredVersionRef?: FilterProductVersion
                ) => {
                    const chatHoveredProduct = lastHoveredRef ? toChatProduct(lastHoveredRef) : undefined;
                    const chatHoveredVersion = lastHoveredVersionRef ? toChatVersion(lastHoveredVersionRef) : undefined;
                    onSubProductClick(subProduct, isSubOnly, chatHoveredProduct, chatHoveredVersion);
                }}
                onVersionClick={(version: FilterProductVersion, lastHoveredRef?: FilterProduct) => {
                    const chatVersion = toChatVersion(version);
                    const chatHoveredProduct = lastHoveredRef ? toChatProduct(lastHoveredRef) : undefined;
                    onVersionClick(chatVersion, chatHoveredProduct);
                }}
                handleTopPosition={handleTopPosition}
                isWapUser={isWapUser}
                isAdmin={isAdmin}
                enableAllProductForAllUsers={enableAllProductForAllUsers}
                enableAllProductForUser={enableAllProductForUser}
                docTypeFilter={docTypeFilter}
                categories={categories}
                chatModel={chatModel}
                reasoningEffort={reasoningEffort}
                aiModel_admin={aiModel_admin}
                templateAppliedFlagValue={templateAppliedFlagValue}
                activeAnalysisPanelTab={activeAnalysisPanelTab}
                applyRestriction={applyRestriction}
                isStreaming={isStreaming}
                isLoading={isLoading}
                isAnswerLoading={isAnswerLoading}
                activateTemplateTour={activateTemplateTour}
                setActivateTemplateTour={setActivateTemplateTour}
                styles={toolbarStyles ?? styles}
                onDocTypeFilterChange={onDocTypeFilterChange}
                onChatModelChange={onChatModelChange}
                onReasoningEffortChange={onReasoningEffortChange}
                onConfigPanelToggle={onConfigPanelToggle}
                onTemplateIconClick={onTemplateIconClick}
                onTemplateClear={onTemplateClear}
                saveUserSettings={saveUserSettings}
            />
        </div>
    );
};

export const ChatToolbar = memo(ChatToolbarComponent);
